import type {
  WholesalerServiceConfig,
  WholesalerFeatureConfig,
  CarriageOption,
  CountryCarriageConfig,
} from '@/types/services';

/**
 * The single canonical role vocabulary for the whole app — system-scoped roles from `GET /me`,
 * per-workspace roles from `GET /me/workspaces`, and team roles from the team endpoints all
 * normalise onto these three values.
 *
 * - `globalAdmin` — Pingco platform account, the only role that unlocks Admin Settings.
 * - `admin` — full access within a single wholesaler workspace.
 * - `billingAdmin` — billing-scoped access within a single wholesaler workspace.
 *
 * The backend speaks two different spellings for these; never compare a raw wire string to
 * these values, always go through `normalizeRole` (src/utils/normalize-role.ts).
 */
export const ROLES = {
  globalAdmin: 'globalAdmin',
  admin: 'admin',
  billingAdmin: 'billingAdmin',
} as const;

export type RoleType = (typeof ROLES)[keyof typeof ROLES];

export interface Permission {
  canManageAdmins: boolean;
  canManageBilling: boolean;
  canViewReports: boolean;
}

export interface Role {
  id: string;
  type: RoleType;
  permissions: Permission;
}

export interface WholesalerActivity {
  id: string;
  description: string;
  createdAt: string;
  amountDueUsdCents: number;
}

/**
 * Matches the real backend's `WholesalerDto` (GET /wholesalers/current, also referenced from
 * GET /me/workspaces) — this is deliberately lean. Do not add fields here unless they come from
 * that endpoint; richer per-wholesaler data (service/feature configs, credit thresholds,
 * activity) is fetched separately by the features that need it (see `WholesalerOverview`,
 * `useWholesalerConfig`, etc).
 */
export interface WholesalerDto {
  id: string;
  name: string;
  kycStatus: string | null;
  type: string | null;
  status: string | null;
  credit: number;
  tcapUrl: string | null;
  businessType: string | null;
}

/**
 * Shape returned by GET /overview — richer, wholesaler-scoped aggregate. Real response schema
 * is undocumented in swagger; verify against the live backend before trusting field names
 * beyond what's already wired up. This is the only GET that exposes the wholesaler's stored
 * logo, so the nav and the workspace switcher read `logo` from here too.
 */
export interface WholesalerOverview {
  id: string;
  name: string;
  isActive: boolean;
  isKycVerified?: boolean;
  type: 'prepaid' | 'postpaid';
  status: string;
  credit: number;
  creditThresholds?: { warning?: number; critical?: number; suspension?: number };
  logo: string | null;
  stripeCompanyId: string | null;
  countryId: string;
  currency: string;
  creditBalanceUsdCents?: number;
  serviceConfigs?: WholesalerServiceConfig[];
  featureConfigs?: WholesalerFeatureConfig[];
  carriagePreference?: CarriageOption[];
  countryCarriage?: CountryCarriageConfig[];
  wholesalerActivity?: WholesalerActivity[];
}

export interface WholesalerAdmin {
  wholesalerId: string;
  adminId: string;
  roleId: string;
}

/** Response from POST /auth/token — no refresh token; refresh is cookie-based. */
export interface TokenResponse {
  accessToken: string;
  expiresAt: string;
  tokenType: string;
}

/** Response from GET /me — the caller's identity, no wholesaler/workspace data. */
export interface CurrentUserDto {
  subject: string;
  name: string;
  email: string;
  authType: string;
  roles: string[];
}

/** One entry from GET /me/workspaces — a wholesaler the caller belongs to. */
export interface WorkspaceDto {
  id: string;
  name: string;
  role: string;
}

export interface Admin {
  id: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  lastLogin: string | null;
  loginFailures: number;
  status: 'active' | 'inactive' | 'suspended';
  roles: RoleType[];
  wholesalers: WorkspaceDto[];
  profileSetupComplete: boolean;
}

export type AuthUser = Admin;

export interface AuthState {
  user: Admin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginResponse {
  user: Admin;
  accessToken: string;
  refreshToken?: string;
}

export interface LoginErrorResponse {
  reason?: string;
  email?: string;
}
